import React, {
  useCallback,
  useState,
  Suspense,
  useContext,
  useRef,
} from 'react';
import RutinUrvalListQuery from '../../../../../../Components/Urval/Components/SavedUrval/Queries/RutinUrvalListQuery/RutinUrvalListQuery';
import { useExportToExcel } from '../../../../../../Components/ExportToExcel/ExportToExcel';
import { IProps as IMenuProps } from './BusinessCollectionMenu';
import EDIcon from '../../../../../../BaseComponents/Icon/EDIcon';
import { useBoolState, useToggle } from '../../../../../../Hooks/booleanHooks';
import UrvalDialog from '../../../../../../Components/Urval/Components/UrvalDialog/UrvalDialog';
import KolumnValjareDialog from '../../../../../../Components/KolumnValjare/Components/KolumnValjareDialog/KolumnValjareDialog';
import {
  findAddComp,
  findBusinessColors,
} from '../../../Const/BusinessConfigs';
import EDMenuButton from '../../../../../../BaseComponents/Buttons/MenuButton/EDMenuButton';
import { BusinessDataFormContext } from '../BusinessDataForm/BusinessDataForm';
import LegendMenu from '../../../../../../Components/Legend/LegendMenu';
import { ICollectionDataType } from '../Queries/BusinessCollectionQuery';
import {
  allDefaultBusinessCollectionMenuItem,
  BusinessCollectionMenuItemsType,
  IBusinessCollectionMenuItem,
  BusinessCollectionMenuItemHookType,
  IBusinessCollectionMenuItemParams,
} from './Utils/BusinessCollectionMenuUtils';
import FunctionItem from '../../FunctionMenu/FunctionItem/FunctionItem';
import HighlightedBusinessCollectionMenuItem from './Components/HighlightedBusinessCollectionMenuItem';
import { IFormData } from '../BusinessDataForm/Utils/BusinessDataFormUtils';

interface IProps extends IMenuProps {
  disableCreateNew?: boolean;
  inCard?: boolean;
  onAddNewItem?: (newItem: ICollectionDataType) => void;
  menuItems?: BusinessCollectionMenuItemsType;
  highlightedBusinessCollectionMenuItem?: string;
}

const BusinessCollectionMenuButton: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { refetch } = props;
  const formContext = useContext(BusinessDataFormContext);
  const [urvalOpen, onOpenUrval, onCloseUrval] = useBoolState(false);
  const [newOpen, onOpenNew, onCloseNew] = useBoolState(false);
  const [open, onOpen, onClose] = useBoolState(false);
  const [tabValue, setTabValue] = useState(0);
  const [legendOpen, toggleLegendOpen] = useToggle();
  const menuRef = useRef<HTMLButtonElement | null>(null);
  const businessColors = findBusinessColors(props.collection.CollectionName);
  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);
  const handleCloseNew = React.useCallback(
    (skipRefetch?: boolean) => {
      onCloseNew();
      if (!skipRefetch) {
        handleRefetch();
      }
    },
    [handleRefetch, onCloseNew]
  );
  const AddComp = findAddComp(props.collection.CollectionName);
  const title = `Lägg till ${props.collection.RootType.SOLabel}`;
  const onColumnsClick = useCallback(() => {
    setTabValue(0);
    onOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const exportToExcel = useExportToExcel({
    collectionName: props.collection.CollectionName,
    urval: props.urval,
    columns: props.columns,
    sortProperties: props.sortedData.properties,
  });

  const getMenuItem = (
    menuItemParam:
      | IBusinessCollectionMenuItem
      | BusinessCollectionMenuItemHookType,
    index: number,
    menuItems: BusinessCollectionMenuItemsType
  ): React.ReactNode => {
    let menuItem: IBusinessCollectionMenuItem;
    if (typeof menuItemParam === 'function') {
      menuItem = menuItemParam();
    } else {
      menuItem = menuItemParam;
    }

    const params: IBusinessCollectionMenuItemParams = {
      collection: props.collection,
      onAddClick: onOpenNew,
      onColumnsClick: onColumnsClick,
      onUrvalClick: onOpenUrval,
      onRefetchClick: handleRefetch,
      onExportExcelClick: exportToExcel,
      onLegendClick: toggleLegendOpen,
    };

    const disabled =
      typeof menuItem.isDisabled === 'function'
        ? menuItem.isDisabled(params)
        : menuItem.isDisabled;
    const disabledReason = typeof disabled !== 'boolean' ? disabled : undefined;

    const onClick = () => {
      menuItem.onClick(params);
    };

    const label =
      typeof menuItem.label === 'function'
        ? menuItem.label(params)
        : menuItem.label;

    if (props.highlightedBusinessCollectionMenuItem === menuItem.id) {
      return null;
    } else if (menuItems.length === 1) {
      return (
        <HighlightedBusinessCollectionMenuItem
          menuItem={menuItem}
          label={label}
          disabled={disabled}
          disabledReason={disabledReason}
          onClick={onClick}
        />
      );
    } else {
      return (
        <FunctionItem
          iconType={menuItem.iconType}
          onClick={onClick}
          disabled={Boolean(disabled)}
          disabledReason={disabledReason}
          key={menuItem.id}
        >
          {label}
        </FunctionItem>
      );
    }
  };

  const items: React.ReactNode[] = props.menuItems
    ? props.menuItems.map(getMenuItem)
    : allDefaultBusinessCollectionMenuItem.map(getMenuItem);

  let button: React.ReactNode;
  if (items && items.length === 1) {
    const menuItem = items[0];
    button = menuItem;
  } else if (items.length === 0) {
    button = null;
  } else {
    button = (
      <EDMenuButton
        iconColor="secondary"
        tooltip={
          formContext?.formState.editMode
            ? 'Otillgänglig i redigeringsläge'
            : 'Funktioner'
        }
        icon={<EDIcon color="secondary" iconType="more_vert" size="medium" />}
        disabled={formContext?.formState.editMode}
        buttonRef={menuRef}
        noClickPropagation
      >
        {items}
      </EDMenuButton>
    );
  }

  const highlightedBusinessCollectionMenuItem = props.menuItems
    ? props.menuItems.find(menuItemParam => {
        let menuItem: IBusinessCollectionMenuItem;
        if (typeof menuItemParam === 'function') {
          menuItem = menuItemParam();
        } else {
          menuItem = menuItemParam;
        }

        return menuItem.id === props.highlightedBusinessCollectionMenuItem;
      })
    : allDefaultBusinessCollectionMenuItem.find(menuItem => {
        return menuItem.id === props.highlightedBusinessCollectionMenuItem;
      });

  let highlightedBusinessCollectionMenuItemButton: React.ReactNode | null =
    null;
  if (highlightedBusinessCollectionMenuItem) {
    let menuItem: IBusinessCollectionMenuItem;
    if (typeof highlightedBusinessCollectionMenuItem === 'function') {
      menuItem = highlightedBusinessCollectionMenuItem();
    } else {
      menuItem = highlightedBusinessCollectionMenuItem;
    }

    const params: IBusinessCollectionMenuItemParams = {
      collection: props.collection,
      onAddClick: onOpenNew,
      onColumnsClick: onColumnsClick,
      onUrvalClick: onOpenUrval,
      onRefetchClick: handleRefetch,
      onExportExcelClick: exportToExcel,
      onLegendClick: toggleLegendOpen,
    };

    const disabled =
      typeof menuItem.isDisabled === 'function'
        ? menuItem.isDisabled(params)
        : menuItem.isDisabled;
    const disabledReason = typeof disabled !== 'boolean' ? disabled : undefined;

    const onClick = () => {
      menuItem.onClick(params);
    };

    const label =
      typeof menuItem.label === 'function'
        ? menuItem.label(params)
        : menuItem.label;

    highlightedBusinessCollectionMenuItemButton = (
      <HighlightedBusinessCollectionMenuItem
        menuItem={menuItem}
        label={label}
        disabled={disabled}
        disabledReason={disabledReason}
        onClick={onClick}
      />
    );
  }

  return (
    <RutinUrvalListQuery
      codeSystemID={undefined}
      collectionName={props.collection.CollectionName}
      routineID={undefined}
      kodplanUrval={false}
    >
      {({ rutinUrvalList, variables }) => (
        <>
          {highlightedBusinessCollectionMenuItemButton}
          {button}
          <Suspense fallback={null}>
            {props.rutinId && (
              <UrvalDialog
                setUrval={props.setUrval || (_ => {})}
                collectionName={props.collection.CollectionName}
                routineID={props.rutinId}
                rutinUrvalList={rutinUrvalList}
                rutinUrvalVariables={variables}
                urval={props.urval}
                open={urvalOpen}
                kodplanUrval={false}
                onClose={onCloseUrval}
                onCommit={() => {
                  props.refetch();
                  onCloseUrval();
                }}
                detaljurvalInfo={props.detaljurvalInfo}
              />
            )}
          </Suspense>
          {props.setColumns && (
            <KolumnValjareDialog
              valdaKolumner={props.columns}
              collectionName={props.collection.CollectionName}
              setValdaKolumner={props.setColumns}
              sortedData={props.sortedData}
              updateSortOrder={props.updateSortOrder}
              tabValue={tabValue}
              setTabValue={setTabValue}
              open={open}
              onClose={onClose}
              inCard={props.inCard}
              inAssociation={
                props.baseCollection.CollectionName !==
                props.collection.CollectionName
              }
            />
          )}
          {AddComp !== undefined && (
            <AddComp
              onCancel={handleCloseNew}
              onValuesCommited={handleCloseNew}
              open={newOpen}
              title={title}
              onClose={handleCloseNew}
              onAddNewItem={(newItem: IFormData) =>
                props.onAddNewItem?.(newItem as ICollectionDataType)
              }
            />
          )}
          <LegendMenu
            posts={businessColors}
            open={legendOpen}
            toggleOpen={toggleLegendOpen}
            menuRef={menuRef}
          />
        </>
      )}
    </RutinUrvalListQuery>
  );
};

export default BusinessCollectionMenuButton;
